import type { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { IndicatorBadge, IndicatorBadgeSizes } from '../../IndicatorBadge';
import { baseColumn } from './baseColumn';
import type { ColDefFactory, Column } from './types';

export interface CountOfParams<TData = any, TItem = any> {
  /** Given a row, return the array of items to count over */
  getItems: (data: TData) => TItem[] | undefined;
  /** Predicate to determine whether an item should be counted. If not provided, all items are counted. */
  shouldCount?: (item: TItem) => boolean;
}

export interface CountOfValue<TItem = any> {
  count: number;
  total: number;
  countedItems: TItem[];
  notCountedItems: TItem[];
  items: TItem[];
}

export const countOf: ColDefFactory<Column<CountOfParams>> = column => ({
  initialWidth: 50,
  ...baseColumn(column),
  headerClass: 'ag-center-aligned-cell',
  cellClass: 'ag-center-aligned-cell',
  valueGetter: ({ data }: ValueGetterParams<unknown>): CountOfValue | undefined => {
    if (!data || !column.params?.getItems) {
      return undefined;
    }

    const items = column.params.getItems(data);
    if (!items) {
      return undefined;
    }

    const countedItems: unknown[] = [];
    const notCountedItems: unknown[] = [];

    // Divide the items into the two buckets
    items.forEach(item => {
      if (column.params?.shouldCount) {
        column.params.shouldCount(item) ? countedItems.push(item) : notCountedItems.push(item);
      } else {
        // all items go into the counted bucket in this case
        countedItems.push(item);
      }
    });

    return { count: countedItems.length, total: items.length, items, countedItems, notCountedItems };
  },
  cellRenderer: ({ value }: ICellRendererParams<unknown, CountOfValue | undefined>) => {
    if (!value) {
      return null;
    }

    return (
      <IndicatorBadge size={IndicatorBadgeSizes.Small}>
        {value.count} / {value.total}
      </IndicatorBadge>
    );
  },
  valueFormatter: ({ value }: ValueFormatterParams<unknown, CountOfValue | undefined>) => {
    if (!value) {
      return '';
    }

    return `${value.count} / ${value.total}`;
  },
});
